import { useQuery } from '@tanstack/react-query';
import { apiClient } from '../lib/api';

export interface RecentTransaction {
  id: string;
  invoiceNo: string;
  invType: number;
  typeName: 'sale' | 'purchase' | 'transfer' | 'issue' | 'issue_return' | 'purchase_return' | 'consumption';
  outlet: string;
  outletName: string;
  amount: number;
  itemCount: number;
  createdBy: string;
  createdAt: string;
  posted: boolean;
}

interface RecentTransactionsResponse {
  data: RecentTransaction[];
  total: number;
}

// Map invtype to transaction type name
export const INVTYPE_MAP: Record<number, RecentTransaction['typeName']> = {
  0: 'purchase',
  1: 'purchase_return',
  2: 'issue',
  3: 'issue_return',
  4: 'transfer',
  5: 'consumption',
  11: 'sale',
};

export function useRecentTransactions(limit: number = 10) {
  return useQuery({
    queryKey: ['dashboard', 'recent-transactions', limit],
    queryFn: async () => {
      const response = await apiClient.get<RecentTransactionsResponse>('/transactions/recent', {
        limit,
      });
      return response;
    },
    refetchInterval: 120000, // Refresh every 2 minutes
    staleTime: 60000, // Consider data stale after 1 minute
    gcTime: 300000, // Keep in cache for 5 minutes
  });
}

export interface PendingInvoice {
  id: string;
  invoiceNo: string;
  invType: number;
  typeName: RecentTransaction['typeName'];
  outlet: string;
  outletName: string;
  amount: number;
  createdBy: string;
  createdAt: string;
  status: 'pending' | 'awaiting_approval';
}

interface PendingInvoicesResponse {
  data: PendingInvoice[];
  total: number;
}

export function usePendingInvoices(limit: number = 10) {
  return useQuery({
    queryKey: ['dashboard', 'pending-invoices', limit],
    queryFn: async () => {
      const response = await apiClient.get<PendingInvoicesResponse>('/transactions/pending', {
        limit,
      });
      return response;
    },
    refetchInterval: 60000, // Refresh every minute
    staleTime: 30000,
  });
}
